import { useContext, useEffect, useState } from 'react'

import { LoadingOverlay } from '@hhgtech/hhg-components/mantine'
import { useDisclosure } from '@mantine/hooks'
import dynamic from 'next/dynamic'

import { OnboardingLeadProps } from 'components/molecules/onboardingLead'
import { CollectingDataFormType } from 'interfaces/types'
import { Information } from 'interfaces/types/DueDate'
import { AppContext } from 'state/appContext'
import { useModalStore } from 'state/modal'
import { PhoneVerifyType } from 'state/types'
import { fastdom } from 'utils/fastdom'

import OnboardingModal from './onboardingModal'
import SimilacOnboardingModal from './similacOnboardingModal'

const OnboardingLead = dynamic(
  () =>
    import('components/molecules/onboardingLead').then(
      (res) => res.OnboardingLead,
    ),
  {
    ssr: false,
  },
)

export type Props = {
  campaign?: OnboardingLeadProps['campaign']
  phoneVerify?: PhoneVerifyType
  isSkippable?: boolean
  similacFormData?: {
    leadCampaignCode?: string
    termContent?: string
    bannerMobile?: string
    bannerDesktop?: string
    thanksPageInfo?: Information
    incentiveMessage?: Information
  }
  typeForm?: CollectingDataFormType
  toolName?: string
  extraPayloadValues?: OnboardingLeadProps['extraPayloadValues']
}

export const OnboardingFlow = ({
  campaign,
  phoneVerify = PhoneVerifyType.SMS,
  isSkippable,
  similacFormData,
  typeForm = CollectingDataFormType.NONE,
  toolName,
  extraPayloadValues,
}: Props) => {
  const {
    state: { userInfo },
  } = useContext(AppContext)

  const [isReady, setIsReady] = useState(false)

  const [opened, { open, close }] = useDisclosure(false, {
    onClose: () => {
      fastdom.mutate(() => {
        document.body.style.overflow = ''
        document.body.classList.remove('noscroll')
      })
    },
    onOpen: () => {
      fastdom.mutate(() => {
        document.body.style.overflow = 'hidden'
        document.body.classList.add('noscroll')
      })
    },
  })

  const shouldOnboardingModalOpen = useModalStore(
    (state) => state.shouldOnboardingModalOpen,
  )

  useEffect(() => {
    try {
      if (!shouldOnboardingModalOpen) {
        close()
      }
      if (
        shouldOnboardingModalOpen &&
        typeForm !== CollectingDataFormType.NONE &&
        userInfo?.id &&
        (typeForm === CollectingDataFormType.LEADGEN_FORM && campaign
          ? true
          : typeForm === CollectingDataFormType.PHONE_ONLY
          ? phoneVerify === PhoneVerifyType.SMS
            ? !userInfo?.phone || !userInfo?.phone_verified_at
            : phoneVerify === PhoneVerifyType.WHATSAPP
            ? !Boolean(userInfo?.whatsapp_verify)
            : false
          : false)
      ) {
        open()
      }
    } catch {}
    if (typeof userInfo !== 'undefined') {
      setIsReady(true)
    }
  }, [
    typeof userInfo,
    userInfo?.id,
    userInfo?.phone,
    userInfo?.phone_verified_at,
    userInfo?.whatsapp_verify,
    shouldOnboardingModalOpen,
  ])
  useEffect(() => {
    if (!isReady && typeForm !== CollectingDataFormType.LEADGEN_FORM) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
  }, [isReady])
  if (!isReady) return <LoadingOverlay visible overlayOpacity={1} />
  const withSimilac = !!similacFormData

  return withSimilac ? (
    opened ? (
      <SimilacOnboardingModal
        isSkippable={isSkippable}
        leadCampaignCode={similacFormData?.leadCampaignCode}
        termContent={similacFormData?.termContent}
        bannerMobile={similacFormData?.bannerMobile}
        bannerDesktop={similacFormData?.bannerDesktop}
        thanksPageInfo={similacFormData?.thanksPageInfo}
        incentiveMessage={similacFormData?.incentiveMessage}
        onClose={close}
      />
    ) : null
  ) : typeForm === CollectingDataFormType.LEADGEN_FORM ? (
    <OnboardingLead
      toolName={toolName}
      extraPayloadValues={extraPayloadValues}
      campaign={campaign}
      opened={opened}
      onClose={close}
    />
  ) : opened ? (
    <OnboardingModal
      isSkippable={isSkippable}
      onClose={close}
      isWhatsApp={
        typeForm === CollectingDataFormType.PHONE_ONLY &&
        phoneVerify === PhoneVerifyType.WHATSAPP
      }
    />
  ) : null
}
